import React, { useEffect, useState } from 'react';
import { api, AED, AED_short } from '../lib/api';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sunrise, Loader2, RefreshCcw, Home, Gavel, TrendingDown, TrendingUp,
  MapPin, Sparkles, Timer, Building2, ArrowRight, AlertTriangle, Trophy, Newspaper,
} from 'lucide-react';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value, sub, accent = 'text-slate-800' }) {
  return (
    <div className="glass p-4 flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-white/70 grid place-items-center shrink-0">
        <Icon size={16} className={accent}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`font-serif text-2xl leading-tight ${accent}`}>{value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function PickCard({ pick, kind }) {
  const isSteal = kind === 'steal';
  const pct = pick.discount_vs_baseline_pct;
  return (
    <div className={`rounded-2xl p-4 border ${
      isSteal ? 'bg-emerald-50/50 border-emerald-200' : 'bg-red-50/50 border-red-200'
    }`} data-testid={`digest-${kind}-${pick.dld_id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <MapPin size={10}/> {pick.community}
          </div>
          <div className="font-serif text-lg">{AED(pick.price_aed)}</div>
          <div className="text-xs text-slate-500">
            {pick.size_sqft ? `${Math.round(pick.size_sqft).toLocaleString()} sqft · ` : ''}
            {pick.aed_per_sqft ? `${Math.round(pick.aed_per_sqft).toLocaleString()} AED/psf` : ''}
          </div>
        </div>
        <div className={`text-right ${isSteal ? 'text-emerald-700' : 'text-red-700'}`}>
          <div className="font-serif text-xl leading-none">{pct != null ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%` : '—'}</div>
          <div className="text-[9px] uppercase tracking-widest">vs community</div>
        </div>
      </div>
      <div className="text-xs text-slate-600 leading-relaxed">{pick.why}</div>
      {pick.community_baseline_psf && (
        <div className="text-[10px] text-slate-400 mt-1.5">
          Community 90d median: {Math.round(pick.community_baseline_psf).toLocaleString()} psf
        </div>
      )}
    </div>
  );
}

export default function DailyDigest() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/digest/latest');
      setDigest(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load digest');
    } finally { setLoading(false); }
  };

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const r = await api.post('/digest/rebuild');
      setDigest(r.data);
      toast.success('Digest rebuilt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rebuild failed');
    } finally { setRebuilding(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="glass p-12 text-center text-slate-400 flex items-center justify-center gap-2">
        <Loader2 size={20} className="animate-spin"/> Loading Iris Morning Brief…
      </div>
    </div>
  );
  if (!digest) return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="glass p-12 text-center text-slate-500">Nothing to show yet. Try the Rebuild button in the header.</div>
    </div>
  );

  const pulse = digest.market_pulse || {};
  const txns = pulse.transactions || {};
  const listings = pulse.new_listings || {};
  const picks = digest.ai_picks || {};
  const fresh = digest.fresh_deal_radar || [];
  const closing = digest.auctions_closing_24h || [];

  return (
    <div className="max-w-6xl mx-auto p-4 lg:p-8 space-y-6" data-testid="daily-digest-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className="relative rounded-3xl overflow-hidden border border-white/70 shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A2530] via-[#243544] to-[#0F1720]"/>
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-500/25 blur-3xl"/>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-[#679286]/25 blur-3xl"/>
        <div className="relative p-6 flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm grid place-items-center shrink-0">
            <Sunrise size={26} className="text-amber-300"/>
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1">
              <Sparkles size={10}/> Iris AI · Morning Brief
            </div>
            <h1 className="font-serif text-3xl text-white leading-tight">Yesterday in Dubai property</h1>
            <div className="text-sm text-white/70 mt-1">
              {digest.date} · Generated {digest.generated_at ? new Date(digest.generated_at).toLocaleString() : '—'}
            </div>
            {picks.headline && (
              <div className="mt-3 rounded-xl bg-white/10 backdrop-blur-sm px-4 py-2.5 text-sm text-white/90 italic">
                &ldquo;{picks.headline}&rdquo;
              </div>
            )}
          </div>
          <button onClick={rebuild} disabled={rebuilding}
            data-testid="digest-rebuild"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs">
            {rebuilding ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
            Rebuild
          </button>
        </div>
      </motion.div>

      {/* Market pulse */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
          <TrendingUp size={11}/> Market pulse
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={Home}
            label="New listings"
            value={(listings.total || 0).toLocaleString()}
            sub={(listings.by_source || []).map(s => `${s.count} ${s.source === 'bayutapi_v3' ? 'Bayut' : (s.source || 'other')}`).join(' · ')}
            accent="text-slate-800"
          />
          <StatCard
            icon={Building2}
            label="Transactions closed"
            value={(txns.count || 0).toLocaleString()}
            sub={`Total: ${AED_short(txns.total_value_aed)} AED`}
            accent="text-[#679286]"
          />
          <StatCard
            icon={TrendingUp}
            label="Avg AED/psf"
            value={txns.avg_psf ? Math.round(txns.avg_psf).toLocaleString() : '—'}
            sub={txns.psf_delta_pct != null ? `${txns.psf_delta_pct > 0 ? '+' : ''}${txns.psf_delta_pct}% vs 30d` : 'no baseline'}
            accent={txns.psf_delta_pct >= 0 ? 'text-emerald-700' : 'text-red-700'}
          />
          <StatCard
            icon={MapPin}
            label="Hottest community"
            value={(txns.top_communities || [])[0]?.community || '—'}
            sub={(txns.top_communities || [])[0] ? `${(txns.top_communities || [])[0].count} deals` : ''}
            accent="text-amber-700"
          />
        </div>
      </div>

      {/* AI picks */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
          <Sparkles size={11} className="text-[#679286]"/> Iris AI · Yesterday&apos;s verdicts
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="glass p-5">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={16} className="text-emerald-600"/>
              <h3 className="font-serif text-lg">Top steals</h3>
              <span className="text-[10px] text-slate-400 ml-auto">15%+ below market</span>
            </div>
            {(picks.steals || []).length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">
                No standout steals in yesterday&apos;s data{picks.sample_size ? ` (${picks.sample_size} txns reviewed).` : '.'}
              </div>
            ) : (
              <div className="space-y-3">
                {(picks.steals || []).map((p, i) => <PickCard key={`s${i}`} pick={p} kind="steal"/>)}
              </div>
            )}
          </div>
          <div className="glass p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={16} className="text-red-600"/>
              <h3 className="font-serif text-lg">Overpriced sales</h3>
              <span className="text-[10px] text-slate-400 ml-auto">15%+ above market</span>
            </div>
            {(picks.overpriced || []).length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">No obvious overpays yesterday.</div>
            ) : (
              <div className="space-y-3">
                {(picks.overpriced || []).map((p, i) => <PickCard key={`o${i}`} pick={p} kind="overpriced"/>)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top communities */}
      {(txns.top_communities || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
            <MapPin size={11}/> Yesterday&apos;s most active communities
          </div>
          <div className="glass overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left py-2.5 px-3">Community</th>
                  <th className="text-right py-2.5 px-3">Deals</th>
                  <th className="text-right py-2.5 px-3">Total AED</th>
                  <th className="text-right py-2.5 px-3">Avg psf</th>
                </tr>
              </thead>
              <tbody>
                {(txns.top_communities || []).slice(0, 8).map((c, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-2.5 px-3 font-medium">{c.community}</td>
                    <td className="py-2.5 px-3 text-right text-slate-700">{c.count}</td>
                    <td className="py-2.5 px-3 text-right text-slate-700">{AED_short(c.total_value_aed)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-700">
                      {c.median_psf ? Math.round(c.median_psf).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fresh Deal Radar */}
      {fresh.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1"><Sparkles size={11} className="text-[#679286]"/> Current Deal Radar</span>
            <Link to="/app/trends" className="text-[10px] text-[#679286] hover:underline inline-flex items-center gap-1">
              Open Deal Radar <ArrowRight size={10}/>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {fresh.map((d, i) => (
              <div key={i} className="glass p-3" data-testid={`digest-fresh-${d.source}-${d.source_id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
                    d.source === 'auction' ? 'bg-purple-600 text-white' : 'bg-emerald-600 text-white'
                  }`}>{d.source === 'auction' ? 'AUCT' : 'LIST'}</span>
                  <div className="text-xs text-slate-500 truncate">{d.community || '—'}</div>
                  <div className="ml-auto font-serif text-lg">{d.hotness_score || 0}</div>
                </div>
                <div className="text-xs text-slate-700 line-clamp-2 min-h-[2rem]">{d.verdict}</div>
                <div className="text-sm font-semibold text-slate-900 mt-1">{AED_short(d.price_aed)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auctions closing */}
      {closing.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
            <Gavel size={11}/> Auctions closing in the next 24h
          </div>
          <div className="glass overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left py-2.5 px-3">Lot</th>
                  <th className="text-left py-2.5 px-3">Community</th>
                  <th className="text-right py-2.5 px-3">Bid</th>
                  <th className="text-right py-2.5 px-3">Bids</th>
                  <th className="text-right py-2.5 px-3">Closes in</th>
                  <th className="text-right py-2.5 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {closing.map((a) => (
                  <tr key={a.external_id} className="border-t border-slate-100">
                    <td className="py-2.5 px-3 font-medium text-slate-800 line-clamp-1 max-w-[300px]">{a.title || '—'}</td>
                    <td className="py-2.5 px-3 text-slate-700 text-xs">{a.community || '—'}</td>
                    <td className="py-2.5 px-3 text-right text-slate-800">{AED_short(a.current_bid_aed || a.starting_bid_aed)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-500 text-xs">{a.bid_count || 0}</td>
                    <td className="py-2.5 px-3 text-right text-xs">
                      <span className={`inline-flex items-center gap-1 font-medium ${
                        a.hours_left <= 4 ? 'text-red-600' : a.hours_left <= 12 ? 'text-amber-600' : 'text-slate-700'
                      }`}>
                        <Timer size={10}/> {a.hours_left != null ? `${a.hours_left}h` : '—'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <Link to={`/app/auctions?open=${a.external_id}`} className="text-xs text-[#679286] hover:underline">Open →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-center text-[10px] text-slate-400 pt-4">
        <Newspaper size={11} className="inline mr-1"/>
        UAE real-estate news · coming soon
      </div>
    </div>
  );
}
